import { useEffect, useState } from 'react';
import {
  approveDesktopPairing,
  getDesktopPairingRequest,
} from '../../services/api.js';
import { normalizePairingCode } from '../../utils/desktopPairing.js';
import styles from './AuthPages.module.css';

export default function DesktopPairingPage({ currentUser, onLogout }) {
  const initialCode = normalizePairingCode(new URLSearchParams(window.location.search).get('code'));
  const [code, setCode] = useState(initialCode);
  const [request, setRequest] = useState(null);
  const [state, setState] = useState('idle');
  const [message, setMessage] = useState('');

  async function lookUp(pairingCode = code) {
    const normalized = normalizePairingCode(pairingCode);
    if (normalized.length !== 9) {
      setState('error');
      setMessage('Enter the eight-character code shown by the Windows app.');
      return;
    }
    setState('loading');
    setMessage('');
    setRequest(null);
    try {
      const details = await getDesktopPairingRequest(normalized);
      setRequest(details);
      setState('ready');
    } catch (error) {
      setState('error');
      setMessage(error.message || 'Pairing request not found.');
    }
  }

  useEffect(() => {
    if (initialCode.length === 9) lookUp(initialCode);
    // The URL-supplied code is intentionally consumed only on first render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function approve() {
    setState('approving');
    setMessage('');
    try {
      await approveDesktopPairing(code);
      setState('approved');
    } catch (error) {
      setState('error');
      setMessage(error.message || 'Could not approve this Windows app.');
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <img src="/TDA_Logo.jpg" alt="Track Day Analysis" className={styles.logo} />
        <h1 className={styles.title}>Connect Windows app</h1>

        {state === 'approved' ? (
          <>
            <p className={styles.success}>Connected. You can return to Track Day Analysis on your Windows PC.</p>
            <p className={styles.info}>This tab can now be closed.</p>
          </>
        ) : (
          <>
            <p className={styles.info}>
              Enter the code shown by the local app. Only approve a computer you recognize.
            </p>
            <form className={styles.form} onSubmit={event => { event.preventDefault(); lookUp(); }}>
              <input
                className={`${styles.input} ${styles.codeInput}`}
                value={code}
                onChange={event => {
                  setCode(normalizePairingCode(event.target.value));
                  setRequest(null);
                  setState('idle');
                  setMessage('');
                }}
                placeholder="ABCD-2345"
                autoComplete="one-time-code"
                autoFocus
              />
              <button className={styles.btn} disabled={state === 'loading'} type="submit">
                {state === 'loading' ? 'Checking…' : 'Continue'}
              </button>
            </form>

            {request && state !== 'error' && (
              <div className={styles.confirmBox}>
                <p><strong>{request.device_name}</strong> wants permission to upload local sessions.</p>
                <p className={styles.info}>They will be added to {request.account_email}.</p>
                <button className={styles.btn} onClick={approve} disabled={state === 'approving'}>
                  {state === 'approving' ? 'Connecting…' : 'Connect this computer'}
                </button>
              </div>
            )}

            {state === 'error' && <p className={styles.error}>{message}</p>}
            <div className={styles.links}>
              <a className={styles.link} href="/">Return to Track Day Analysis</a>
              {onLogout && <button className={styles.link} onClick={onLogout}>Sign in with another account</button>}
            </div>
            <p className={styles.info}>Signed in as {currentUser?.email}</p>
          </>
        )}
      </div>
    </div>
  );
}
